import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ASSETS } from '../utils/assets';

interface HeaderProps {
  onContactClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onContactClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
      // Schließe das Mobile-Menü wenn das Fenster vergrößert wird
      if (window.innerWidth > 1200) {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
      }
      
      // Setze CSS Custom Property für Header-Höhe
      const header = document.querySelector('header');
      if (header) {
        const headerHeight = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Body scroll lock effect
  useEffect(() => {
    if (isMenuOpen && !isMenuClosing) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen, isMenuClosing]);

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      // Schließ-Animation starten
      setIsMenuClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
      }, 300); // Dauer der schnelleren Collapse-Animation
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleContactClick = () => {
    if (isMenuOpen) {
      // Schließ-Animation starten
      setIsMenuClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
        onContactClick?.();
      }, 300);
    } else {
      onContactClick?.();
    }
  };

  const scrollToSection = (sectionId: string) => {
    const performScroll = () => {
      if (isHomePage) {
        if (sectionId === 'hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const element = document.getElementById(sectionId);
          if (element) {
            const header = document.querySelector('header');
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const offset = 20;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerHeight - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      } else {
        // Weiterleitung zur Homepage mit Hash
        window.location.href = `/#${sectionId}`;
      }
    };

    if (isMenuOpen) {
      // Schließ-Animation starten
      setIsMenuClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
        performScroll();
      }, 300);
    } else {
      performScroll();
    }
  };

  const navigationItems = [
    { label: 'Startseite', target: 'hero', isSection: true },
    { label: 'Dienstleistungen', target: 'services', isSection: true },
    { label: 'Referenzen', target: 'references', isSection: true },
    { label: 'Erfahrungen', target: 'testimonials', isSection: true },
  ];

  return (
    <header className={`${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-expanded' : ''} ${isMenuClosing ? 'menu-closing' : ''} ${isButtonHovered ? 'splash-active' : ''}`}>
      {/* Logo */}
      <Link to="/">
        <img 
          id="logo" 
          src={ASSETS.logo} 
          alt="Stimm Logo"
          loading="lazy"
        />
      </Link>

      {/* Desktop Navigation */}
      {!isMobile && (
        <nav>
          {navigationItems.map((item) => (
            item.isSection ? (
              <a
                key={item.target}
                href={`#${item.target}`}
                data-action="navigation"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.target);
                }}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.target}
                to={item.target}
                onClick={() => {
                  if (isMenuOpen) {
                    setIsMenuOpen(false);
                  }
                }}
              >
                {item.label}
              </Link>
            )
          ))}
          <a 
            href="#" 
            onClick={(e) => { e.preventDefault(); handleContactClick(); }}
            data-action="contact"
          >
            Kontakt
          </a>
        </nav>
      )}

      {/* Mobile Navigation Items direkt im Header */}
      {isMobile && (isMenuOpen || isMenuClosing) && (
        <>
          <nav className="menu-nav-links">
            {navigationItems.map((item) => (
              item.isSection ? (
                <a
                  key={item.target}
                  href={`#${item.target}`}
                  data-action="navigation"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.target);
                  }}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.target}
                  to={item.target}
                  onClick={() => {
                    if (isMenuOpen) {
                      setIsMenuClosing(true);
                      setTimeout(() => {
                        setIsMenuOpen(false);
                        setIsMenuClosing(false);
                      }, 300);
                    }
                  }}
                >
                  {item.label}
                </Link>
              )
            ))}
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleContactClick(); }}
              data-action="contact"
              className="contact-link"
            >
              Kontakt
            </a>
          </nav>
          
          {/* Schließen-Kreuz unter den Menu-Items */}
          <button
            id="close-button"
            onClick={handleMenuToggle}
            aria-label="Menü schließen"
          />
        </>
      )}

      {/* Desktop CTA Button */}
      {!isMobile && (
        <div>
          <button 
            className="cta-button"
            onClick={handleContactClick} 
            data-action="contact"
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            Projekt starten
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      )}

      {/* Mobile Menu Toggle Button */}
      <button 
        id="menu-button" 
        className={isMenuOpen ? 'hidden' : ''}
        onClick={handleMenuToggle}
        aria-label="Menü öffnen"
        style={{ display: isMobile ? 'block' : 'none' }}
      >
        <i className="fas fa-bars"></i>
      </button>
    </header>
  );
};

export default Header;
